import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { PlayCircle, PauseCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { formatTime, type LocaleKey } from '@/lib/time';
import { useShopStatus, useResumeShop } from '@/hooks/useShopStatus';

/**
 * Full-width strip shown while the shop is paused so it's impossible to miss
 * across any screen. Surfaces the auto-resume time (or "until you resume") and
 * the optional reason, plus the Resume action.
 */
export function PausedBanner() {
  const { t, i18n } = useTranslation();
  const { data: status } = useShopStatus();
  const resume = useResumeShop();

  if (!status || !status.paused) return null;

  const until = status.pausedUntil
    ? t('shop.paused.until', { time: formatTime(status.pausedUntil, i18n.language as LocaleKey) })
    : t('shop.paused.indefinite');

  const onResume = () => {
    resume.mutate(undefined, {
      onSuccess: () => toast.success(t('shop.pause.toasts.resumed')),
      onError: (err) => toast.error(t('shop.pause.toasts.failed', { message: err.message })),
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-900 dark:text-amber-200">
      <PauseCircle className="h-5 w-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <span className="font-semibold">{t('shop.paused.title')}</span>
        <span className="mx-1.5 opacity-50">·</span>
        <span>{until}</span>
        {status.reason ? (
          <span className="block truncate text-amber-800/80 dark:text-amber-200/70">
            “{status.reason}”
          </span>
        ) : null}
      </div>
      <Button variant="success" size="sm" onClick={onResume} disabled={resume.isPending}>
        <PlayCircle className="mr-2 h-4 w-4" />
        {t('shop.paused.resume')}
      </Button>
    </div>
  );
}
